import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MembershipsService, type CreateMembershipDto, type DatosComplementarios } from '../../services/memberships/memberships.service';
import { parseFechaLocal } from '../../utils/date.utils';
import { MinistriesService } from '../../services/ministries/ministries.service';
import { NotificationToast } from '../ui/NotificationToast';
import { DatosComplementariosForm } from './DatosComplementariosForm';
import { User, Users, Calendar, CheckCircle, XCircle, Phone, Mail, BookOpen } from 'lucide-react';

const createMemberSchema = z.object({
  nombres: z.string().min(1, 'Nombres es requerido').min(2, 'Mínimo 2 caracteres'),
  apellidos: z.string().min(1, 'Apellidos es requerido').min(2, 'Mínimo 2 caracteres'),
  fechaNacimiento: z.string().min(1, 'Fecha de nacimiento es requerida'),
  genero: z.enum(['Masculino', 'Femenino'], {
    errorMap: () => ({ message: 'Debe seleccionar un género' })
  }),
  estadoCivil: z.enum(['soltero', 'soltera', 'casado', 'casada', 'union_libre', 'viudo', 'viuda', 'divorciado', 'divorciada'], {
    errorMap: () => ({ message: 'Debe seleccionar un estado civil' })
  }),
  telefono: z.string().min(1, 'Teléfono es requerido').min(7, 'Teléfono inválido'),
  email: z.union([
    z.string().email('Email inválido'),
    z.string().length(0)
  ]).optional(),
  zonaBarrio: z.string().min(1, 'Zona/Barrio es requerido'),
  tipoDocumento: z.enum(['cedula', 'tarjeta_identidad', 'pasaporte', 'otro'], {
    errorMap: () => ({ message: 'Debe seleccionar un tipo de documento' })
  }).optional(),
  numeroDocumento: z.string().optional(),
  consentimientoDatos: z.boolean().refine(val => val === true, {
    message: 'Debe aceptar el consentimiento de datos'
  }),
  autorizacionImagen: z.boolean().refine(val => val === true, {
    message: 'Debe aceptar la autorización de uso de imagen'
  }),
  observacionesLegales: z.string().optional()
});

type CreateMemberFormData = z.infer<typeof createMemberSchema>;

interface CreateMemberDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  ministerioId?: string;
}

export const CreateMemberDrawer: React.FC<CreateMemberDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess,
  ministerioId
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createdMember, setCreatedMember] = useState<any>(null);
  const [currentStep, setCurrentStep] = useState<'form' | 'complementary' | 'success'>('form');
  const [isSubmittingComplementary, setIsSubmittingComplementary] = useState(false);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  
  // Estados para dropdowns
  const [generoDropdownOpen, setGeneroDropdownOpen] = useState(false);
  const [estadoCivilDropdownOpen, setEstadoCivilDropdownOpen] = useState(false);
  const [tipoDocumentoDropdownOpen, setTipoDocumentoDropdownOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    control
  } = useForm<CreateMemberFormData>({
    resolver: zodResolver(createMemberSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      nombres: '',
      apellidos: '',
      fechaNacimiento: '',
      telefono: '',
      email: '',
      zonaBarrio: '',
      genero: undefined,
      estadoCivil: undefined,
      tipoDocumento: undefined,
      numeroDocumento: '',
      consentimientoDatos: false,
      autorizacionImagen: false,
      observacionesLegales: ''
    }
  });

  const consentimientoDatos = watch('consentimientoDatos');
  const fechaNacimiento = watch('fechaNacimiento');

  // Calcular edad si hay fecha de nacimiento
  const calcularEdad = (fecha: string) => {
    if (!fecha) return null;
    const hoy = new Date();
    const nacimiento = parseFechaLocal(fecha);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  };

  const edad = fechaNacimiento ? calcularEdad(fechaNacimiento) : null;

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
      // Reset state when closing
      setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        setCreatedMember(null);
        setCurrentStep('form');
        setIsSubmittingComplementary(false);
        setShowNotification(false);
        setNotificationConfig(null);
        reset();
      }, 300);
    }
  }, [isOpen, reset]);

  const handleClose = () => {
    setIsVisible(false);
    // Llamar onClose después de un pequeño delay para permitir la animación
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const onSubmit = async (data: CreateMemberFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const memberData: CreateMembershipDto = {
        nombres: data.nombres,
        apellidos: data.apellidos,
        fechaNacimiento: data.fechaNacimiento,
        genero: data.genero,
        estadoCivil: data.estadoCivil,
        telefono: data.telefono,
        email: data.email || undefined,
        zonaBarrio: data.zonaBarrio,
        tipoDocumento: data.tipoDocumento,
        numeroDocumento: data.numeroDocumento,
        consentimientoDatos: data.consentimientoDatos,
        autorizacionImagen: data.autorizacionImagen,
        observacionesLegales: data.observacionesLegales
      };

      const userDataRaw = localStorage.getItem('userData');
      const userData = userDataRaw ? (JSON.parse(userDataRaw) as { id?: string }) : null;
      const creadoPorId = userData?.id || '';

      const result = await MembershipsService.createMembershipAdmin(memberData, creadoPorId);

      // Si se creó desde un ministerio, agregar automáticamente al ministerio
      if (ministerioId) {
        try {
          await MinistriesService.addIntegrante(ministerioId, { membresiaId: result.id });
        } catch (err) {
          console.error('Error agregando miembro al ministerio:', err);
        }
      }
      
      setCreatedMember({
        id: result.id,
        nombres: result.nombres,
        apellidos: result.apellidos,
        email: result.email,
        telefono: result.telefono
      });

      // Mostrar toast de éxito
      setNotificationConfig({
        title: '¡Miembro creado!',
        message: `${result.nombres} ${result.apellidos} fue registrado exitosamente. Ahora completa los datos complementarios.`,
        type: 'success'
      });
      setShowNotification(true);

      // Avanzar al paso de datos complementarios
      setCurrentStep('complementary');
      
      // Si fue exitoso y hay callback, ejecutarlo
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Error al crear miembro:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al crear miembro';
      setErrorMessage(errorMsg);
      
      // Mostrar toast de error
      setNotificationConfig({
        title: 'Error al crear miembro',
        message: errorMsg,
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    handleClose();
  };

  const handleSaveDatosComplementarios = async (data: Partial<DatosComplementarios>) => {
    if (!createdMember?.id) return;
    
    setIsSubmittingComplementary(true);
    try {
      await MembershipsService.createDatosComplementarios(createdMember.id, data);
      setNotificationConfig({
        title: '¡Encuesta guardada!',
        message: 'Los datos complementarios han sido registrados exitosamente.',
        type: 'success'
      });
      setShowNotification(true);
      setCurrentStep('success');
      
      // Cerrar el drawer después de un momento
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      setNotificationConfig({
        title: 'Error al guardar encuesta',
        message: err.message || 'No se pudieron guardar los datos complementarios.',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsSubmittingComplementary(false);
    }
  };

  const handleSkipComplementary = () => {
    setCurrentStep('success');
    setTimeout(() => {
      handleClose();
    }, 2000);
  };

  const generosOptions = [
    { 
      value: 'Masculino', 
      label: 'Masculino',
      description: 'Hombre',
      icon: User,
      color: 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100'
    },
    { 
      value: 'Femenino', 
      label: 'Femenino',
      description: 'Mujer',
      icon: User,
      color: 'bg-pink-50 border-pink-200 text-pink-800 hover:bg-pink-100'
    }
  ];

  const estadosCiviles = [
    { value: 'soltero', label: 'Soltero', color: 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100' },
    { value: 'soltera', label: 'Soltera', color: 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100' },
    { value: 'casado', label: 'Casado', color: 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100' },
    { value: 'casada', label: 'Casada', color: 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100' },
    { value: 'union_libre', label: 'Unión Libre', color: 'bg-teal-50 border-teal-200 text-teal-800 hover:bg-teal-100' },
    { value: 'viudo', label: 'Viudo', color: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100' },
    { value: 'viuda', label: 'Viuda', color: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100' },
    { value: 'divorciado', label: 'Divorciado', color: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100' },
    { value: 'divorciada', label: 'Divorciada', color: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100' }
  ];

  const tiposDocumento = [
    { value: 'cedula', label: 'Cédula de Ciudadanía', color: 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100' },
    { value: 'tarjeta_identidad', label: 'Tarjeta de Identidad', color: 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100' },
    { value: 'pasaporte', label: 'Pasaporte', color: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100' },
    { value: 'otro', label: 'Otro', color: 'bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100' }
  ];

  // Custom Dropdown Component (mismo del CreateUserDrawer)
  const CustomDropdown: React.FC<{
    value: string;
    onValueChange: (value: string) => void;
    options: Array<{
      value: string;
      label: string;
      description?: string;
      icon?: React.ComponentType<any> | string;
      color?: string;
    }>;
    placeholder: string;
    isOpen: boolean;
    onToggle: () => void;
    error?: boolean;
  }> = ({ value, onValueChange, options, placeholder, isOpen, onToggle, error = false }) => {
    const selectedOption = options.find(option => option.value === value);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          if (isOpen) onToggle();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, onToggle]);

    return (
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={onToggle}
          className={`group w-full px-4 py-3 border rounded-lg transition-all duration-200 text-left flex items-center justify-between shadow-sm ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
              : isOpen 
                ? 'border-purple-500 focus:border-purple-500 focus:ring-purple-500/20 ring-4 ring-purple-500/20 bg-purple-50/30'
                : 'border-gray-300 focus:border-purple-500 hover:border-purple-400 hover:bg-purple-50/20'
          } focus:outline-none`}
        >
          <div className="flex items-center min-w-0 flex-1">
            {selectedOption ? (
              <>
                {selectedOption.icon && (
                  <div className="mr-3 shrink-0">
                    {typeof selectedOption.icon === 'string' ? (
                      <span className="text-xs">{selectedOption.icon}</span>
                    ) : (
                      <selectedOption.icon className="w-5 h-5" />
                    )}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">{selectedOption.label}</div>
                  {selectedOption.description && (
                    <div className="text-xs text-gray-500 truncate mt-0.5">{selectedOption.description}</div>
                  )}
                </div>
              </>
            ) : (
              <span className="text-gray-500 font-medium">{placeholder}</span>
            )}
          </div>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-all duration-200 shrink-0 ml-2 ${
              isOpen ? 'rotate-180 text-purple-500' : 'group-hover:text-purple-400'
            }`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-auto animate-in slide-in-from-top-2 duration-200">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onValueChange(option.value);
                  onToggle();
                }}
                className={`w-full px-4 py-3 text-left transition-all duration-150 border-b border-gray-50 last:border-b-0 first:rounded-t-xl last:rounded-b-xl ${
                  value === option.value 
                    ? 'bg-purple-100 border-purple-200 shadow-sm' 
                    : option.color || 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  {option.icon && (
                    <div className="mr-3 shrink-0">
                      {typeof option.icon === 'string' ? (
                        <span className="text-xs">{option.icon}</span>
                      ) : (
                        <option.icon className="w-5 h-5" />
                      )}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className={`font-medium truncate ${
                      value === option.value ? 'text-purple-900' : ''
                    }`}>
                      {option.label}
                    </div>
                    {option.description && (
                      <div className={`text-xs mt-1 truncate ${
                        value === option.value ? 'text-purple-700' : 'opacity-75'
                      }`}>
                        {option.description}
                      </div>
                    )}
                  </div>
                  {value === option.value && (
                    <CheckCircle className="w-5 h-5 text-purple-600 shrink-0 ml-2" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 top-0 z-[70] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Overlay */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        {/* Drawer */}
        <section className={`absolute right-0 top-0 h-full w-full max-w-4xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-medium text-gray-900">
                  {currentStep === 'form' && 'Crear Nuevo Miembro'}
                  {currentStep === 'complementary' && 'Encuesta Complementaria'}
                  {currentStep === 'success' && 'Registro Completado'}
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Errores de validación */}
              {errorMessage && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 text-xs">{errorMessage}</p>
                  </div>
                </div>
              )}

              {currentStep === 'form' && (
                <>
                  {/* Instrucciones */}
                  <div className="mb-6">
                    <h3 className="text-xs font-medium text-gray-900 mb-3">
                      Registro de nuevo miembro
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <ul className="text-xs text-blue-800 space-y-1">
                        <li>• Completa todos los campos obligatorios marcados con (*)</li>
                        <li>• El email debe ser único en el sistema</li>
                        <li>• El consentimiento de datos es obligatorio</li>
                        <li>• La información puede ser editada posteriormente</li>
                      </ul>
                    </div>
                  </div>

                  {/* Formulario */}
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Información personal básica */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Información Personal
                      </h3>
                      
                      <div className="space-y-4">
                        {/* Nombres y Apellidos */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Nombres *
                            </label>
                            <input
                              type="text"
                              {...register('nombres')}
                              placeholder="Juan Carlos"
                              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                errors.nombres
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.nombres && (
                              <p className="mt-1 text-xs text-red-500">{errors.nombres.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Apellidos *
                            </label>
                            <input
                              type="text"
                              {...register('apellidos')}
                              placeholder="Pérez González"
                              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                errors.apellidos
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.apellidos && (
                              <p className="mt-1 text-xs text-red-500">{errors.apellidos.message}</p>
                            )}
                          </div>
                        </div>

                        {/* Fecha de nacimiento y edad calculada */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Fecha de Nacimiento *
                            </label>
                            <input
                              type="date"
                              {...register('fechaNacimiento')}
                              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                errors.fechaNacimiento
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.fechaNacimiento && (
                              <p className="mt-1 text-xs text-red-500">{errors.fechaNacimiento.message}</p>
                            )}
                          </div>

                          {/* Mostrar edad calculada */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Edad (calculada)
                            </label>
                            <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 flex items-center">
                              <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                              <span className="text-gray-700">
                                {edad !== null ? `${edad} años` : 'Ingresa la fecha de nacimiento'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Género y Estado Civil */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Género *
                            </label>
                            <Controller
                              name="genero"
                              control={control}
                              render={({ field }) => (
                                <CustomDropdown
                                  value={field.value || ''}
                                  onValueChange={field.onChange}
                                  options={generosOptions}
                                  placeholder="Seleccionar género..."
                                  isOpen={generoDropdownOpen}
                                  onToggle={() => setGeneroDropdownOpen(!generoDropdownOpen)}
                                  error={!!errors.genero}
                                />
                              )}
                            />
                            {errors.genero && (
                              <p className="mt-1 text-xs text-red-500">{errors.genero.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Estado Civil *
                            </label>
                            <Controller
                              name="estadoCivil"
                              control={control}
                              render={({ field }) => (
                                <CustomDropdown
                                  value={field.value || ''}
                                  onValueChange={field.onChange}
                                  options={estadosCiviles}
                                  placeholder="Seleccionar estado civil..."
                                  isOpen={estadoCivilDropdownOpen}
                                  onToggle={() => setEstadoCivilDropdownOpen(!estadoCivilDropdownOpen)}
                                  error={!!errors.estadoCivil}
                                />
                              )}
                            />
                            {errors.estadoCivil && (
                              <p className="mt-1 text-xs text-red-500">{errors.estadoCivil.message}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Información de contacto */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Información de Contacto
                      </h3>
                      
                      <div className="space-y-4">
                        {/* Teléfono y Email */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Teléfono *
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Phone className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="tel"
                                {...register('telefono')}
                                placeholder="+57 300 123 4567"
                                className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-all ${
                                  errors.telefono
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                    : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                                } focus:ring-4 focus:outline-none`}
                              />
                            </div>
                            {errors.telefono && (
                              <p className="mt-1 text-xs text-red-500">{errors.telefono.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Email <span className="text-gray-400">(opcional)</span>
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-400" />
                              </div>
                              <input
                                type="email"
                                {...register('email')}
                                placeholder="usuario@ejemplo.com"
                                className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-all ${
                                  errors.email
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                    : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                                } focus:ring-4 focus:outline-none`}
                              />
                            </div>
                            {errors.email && (
                              <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
                            )}
                          </div>
                        </div>

                        {/* Zona/Barrio */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Zona/Barrio *
                          </label>
                          <input
                            type="text"
                            {...register('zonaBarrio')}
                            placeholder="Centro, Chapinero, Zona Norte, etc."
                            className={`w-full px-4 py-3 border rounded-lg transition-all ${
                              errors.zonaBarrio
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                            } focus:ring-4 focus:outline-none`}
                          />
                          {errors.zonaBarrio && (
                            <p className="mt-1 text-xs text-red-500">{errors.zonaBarrio.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Documentos (opcional) */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Documentos de Identidad (Opcional)
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Tipo de Documento
                            </label>
                            <Controller
                              name="tipoDocumento"
                              control={control}
                              render={({ field }) => (
                                <CustomDropdown
                                  value={field.value || ''}
                                  onValueChange={field.onChange}
                                  options={tiposDocumento}
                                  placeholder="Seleccionar tipo..."
                                  isOpen={tipoDocumentoDropdownOpen}
                                  onToggle={() => setTipoDocumentoDropdownOpen(!tipoDocumentoDropdownOpen)}
                                  error={!!errors.tipoDocumento}
                                />
                              )}
                            />
                            {errors.tipoDocumento && (
                              <p className="mt-1 text-xs text-red-500">{errors.tipoDocumento.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                              Número de Documento
                            </label>
                            <input
                              type="text"
                              {...register('numeroDocumento')}
                              placeholder="123456789"
                              className={`w-full px-4 py-3 border rounded-lg transition-all ${
                                errors.numeroDocumento
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.numeroDocumento && (
                              <p className="mt-1 text-xs text-red-500">{errors.numeroDocumento.message}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Consentimientos */}
                    <div>
                      <h3 className="text-xs font-medium text-gray-900 mb-3">
                        Consentimientos y Autorizaciones
                      </h3>
                      
                      <div className="space-y-4">
                        {/* Consentimiento de datos */}
                        <div className="flex items-start">
                          <div className="flex items-center h-5 mt-1">
                            <input
                              id="consentimientoDatos"
                              type="checkbox"
                              {...register('consentimientoDatos')}
                              className={`w-5 h-5 rounded border-2 transition-all ${
                                errors.consentimientoDatos
                                  ? 'border-red-500 focus:border-red-500'
                                  : 'border-gray-300 focus:border-purple-500'
                              } text-purple-600 focus:ring-purple-500/20 focus:ring-2`}
                            />
                          </div>
                          <div className="ml-3">
                            <label htmlFor="consentimientoDatos" className="text-xs font-medium text-gray-900">
                              Consentimiento de Datos Personales *
                            </label>
                            <p className="text-xs text-gray-600 mt-1">
                              Autorizo el tratamiento de mis datos personales conforme a la política de privacidad de la organización.
                            </p>
                          </div>
                        </div>
                        {errors.consentimientoDatos && (
                          <p className="text-xs text-red-500 ml-8">{errors.consentimientoDatos.message}</p>
                        )}

                        {/* Autorización de imagen */}
                        <div className="flex items-start">
                          <div className="flex items-center h-5 mt-1">
                            <input
                              id="autorizacionImagen"
                              type="checkbox"
                              {...register('autorizacionImagen')}
                              className={`w-5 h-5 rounded border-2 transition-all ${
                                errors.autorizacionImagen
                                  ? 'border-red-500 focus:border-red-500'
                                  : 'border-gray-300 focus:border-purple-500'
                              } text-purple-600 focus:ring-purple-500/20 focus:ring-2`}
                            />
                          </div>
                          <div className="ml-3">
                            <label htmlFor="autorizacionImagen" className="text-xs font-medium text-gray-900">
                              Autorización de Uso de Imagen *
                            </label>
                            <p className="text-xs text-gray-600 mt-1">
                              Autorizo el uso de mi imagen en actividades y material promocional de la organización.
                            </p>
                          </div>
                        </div>
                        {errors.autorizacionImagen && (
                          <p className="text-xs text-red-500 ml-8">{errors.autorizacionImagen.message}</p>
                        )}

                        {/* Observaciones legales */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Observaciones Legales
                          </label>
                          <textarea
                            {...register('observacionesLegales')}
                            placeholder="Observaciones adicionales sobre consentimientos o restricciones especiales..."
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-purple-500/20 focus:ring-4 focus:outline-none transition-all"
                          />
                        </div>

                        {/* Mensaje informativo de consentimiento */}
                        <div className={`border rounded-lg p-3 ${
                          consentimientoDatos ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
                        }`}>
                          <p className={`text-xs ${
                            consentimientoDatos ? 'text-green-800' : 'text-yellow-800'
                          }`}>
                            {consentimientoDatos ? (
                              <>✅ <strong>Consentimiento otorgado:</strong> Se ha autorizado el tratamiento de datos personales.</>
                            ) : (
                              <>⚠️ <strong>Consentimiento requerido:</strong> Es necesario autorizar el tratamiento de datos para proceder con el registro.</>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </form>
                </>
              )}

              {currentStep === 'complementary' && (
                <div className="space-y-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h3 className="text-xs font-medium text-purple-900 mb-1 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2" />
                      Datos Complementarios
                    </h3>
                    <p className="text-xs text-purple-700">
                      Completa la encuesta de información adicional para <strong>{createdMember?.nombres} {createdMember?.apellidos}</strong>.
                    </p>
                  </div>
                  <DatosComplementariosForm
                    initialData={null}
                    onSubmit={handleSaveDatosComplementarios}
                    onCancel={handleSkipComplementary}
                    isSubmitting={isSubmittingComplementary}
                  />
                </div>
              )}

              {currentStep === 'success' && (
                /* Resultados de la creación */
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xs font-medium text-gray-900 mb-2">
                      Miembro registrado exitosamente
                    </h3>
                  </div>

                  {/* Información del miembro creado */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Información del miembro</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Nombre completo:</span>
                        <span className="text-xs font-medium text-gray-900">{createdMember.nombres} {createdMember.apellidos}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Email:</span>
                        <span className="text-xs font-medium text-gray-900">{createdMember.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Teléfono:</span>
                        <span className="text-xs font-medium text-gray-900">{createdMember.telefono}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">ID:</span>
                        <span className="text-xs font-mono text-gray-900">{createdMember.id}</span>
                      </div>
                    </div>
                  </div>

                  {/* Información adicional */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-xs text-blue-800 font-medium">Miembro registrado con éxito</p>
                        <p className="text-xs text-blue-700 mt-1">El nuevo miembro ya aparece en la base de datos y puede participar en las actividades de la organización.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              {currentStep === 'form' && (
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isLoading || !isValid}
                    className="flex-1 bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    {isLoading ? 'Creando Miembro...' : 'Crear Miembro'}
                  </button>
                </div>
              )}

              {currentStep === 'complementary' && (
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleSkipComplementary}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Omitir encuesta
                  </button>
                </div>
              )}

              {currentStep === 'success' && (
                <button
                  onClick={handleContinue}
                  className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  Continuar
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default CreateMemberDrawer;